/**
 * Vehicles/Fleet Management Page
 * ==============================
 * صفحة إدارة المركبات والأسطول
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';
import { useDialog } from '../../../context/DialogContext';

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  truck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  warning: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  wrench: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
};

const VehiclesPage = () => {
  const navigate = useNavigate();
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();
  const { confirm } = useDialog();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({ plate_number: '', make: '', model: '', year: '', capacity_tons: '', fuel_type: '' });

  const loadVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filter !== 'all') params.append('status', filter);
      const data = await apiCall(`/tenant/vehicles?${params}`);
      setVehicles(data.items || []);
    } catch (err) {
      showError('فشل تحميل المركبات');
    } finally {
      setLoading(false);
    }
  }, [apiCall, search, filter, showError]);

  useEffect(() => { loadVehicles(); }, [loadVehicles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVehicle) {
        await apiCall(`/tenant/vehicles/${editingVehicle.id}`, { method: 'PUT', body: JSON.stringify(formData) });
        success('تم تحديث المركبة بنجاح');
      } else {
        await apiCall('/tenant/vehicles', { method: 'POST', body: JSON.stringify(formData) });
        success('تم إضافة المركبة بنجاح');
      }
      setShowModal(false);
      setEditingVehicle(null);
      setFormData({ plate_number: '', make: '', model: '', year: '', capacity_tons: '', fuel_type: '' });
      loadVehicles();
    } catch (err) {
      showError(err.message || 'فشلت العملية');
    }
  };

  const handleEdit = (v) => {
    setEditingVehicle(v);
    setFormData({ plate_number: v.plate_number || '', make: v.make || '', model: v.model || '', year: v.year || '', capacity_tons: v.capacity_tons || '', fuel_type: v.fuel_type || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'إيقاف المركبة', message: 'هل أنت متأكد من إيقاف هذه المركبة؟', type: 'warning', confirmText: 'إيقاف', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/tenant/vehicles/${id}`, { method: 'DELETE' });
      success('تم إيقاف المركبة بنجاح');
      loadVehicles();
    } catch (err) {
      showError('فشل إيقاف المركبة');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('ar-SA') : '-';
  const isExpiringSoon = (d) => { if (!d) return false; const diff = (new Date(d) - new Date()) / (1000 * 60 * 60 * 24); return diff <= 30 && diff > 0; };
  const isExpired = (d) => d && new Date(d) < new Date();

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__title">
          <h1>إدارة الأسطول</h1>
          <p>{vehicles.length} مركبة</p>
        </div>
        <button className="btn btn--primary" onClick={() => navigate('/tenant/vehicles/add')}>
          {I.plus} <span>إضافة مركبة</span>
        </button>
      </div>

      <div className="page-filters">
        <div className="search-box">
          {I.search}
          <input type="text" placeholder="بحث برقم اللوحة..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
          <option value="all">جميع الحالات</option>
          <option value="active">نشط</option>
          <option value="maintenance">صيانة</option>
          <option value="inactive">غير نشط</option>
        </select>
      </div>

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : vehicles.length === 0 ? (
        <div className="page-empty">{I.truck}<h3>لا يوجد مركبات</h3><p>ابدأ بإضافة مركبات جديدة</p></div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>المركبة</th>
                <th>رقم اللوحة</th>
                <th>السعة</th>
                <th>السائق</th>
                <th>الرخصة</th>
                <th>التأمين</th>
                <th>الحالة</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map(v => (
                <tr key={v.id} onClick={() => navigate(`/tenant/vehicles/${v.id}`)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="cell-user">
                      <div className="cell-user__avatar" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>{I.truck}</div>
                      <div className="cell-user__info">
                        <span className="cell-user__name">{v.make || v.brand} {v.model}</span>
                        <span className="cell-user__email">{v.year}</span>
                      </div>
                    </div>
                  </td>
                  <td dir="ltr" style={{ fontWeight: '600' }}>{v.plate_number} {v.plate_letters}</td>
                  <td>{v.capacity_tons} طن</td>
                  <td>{v.driver?.full_name || '-'}</td>
                  <td>
                    <span style={{ color: isExpired(v.registration_expiry) ? 'var(--tenant-danger)' : isExpiringSoon(v.registration_expiry) ? 'var(--tenant-warning)' : 'var(--tenant-text)' }}>
                      {formatDate(v.registration_expiry)}
                      {isExpiringSoon(v.registration_expiry) && <span style={{ marginRight: '4px' }}>{I.warning}</span>}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: isExpired(v.insurance_expiry) ? 'var(--tenant-danger)' : isExpiringSoon(v.insurance_expiry) ? 'var(--tenant-warning)' : 'var(--tenant-text)' }}>
                      {formatDate(v.insurance_expiry)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge--${v.status === 'active' ? 'success' : v.status === 'maintenance' ? 'warning' : 'danger'}`}>
                      {v.status === 'active' ? 'نشط' : v.status === 'maintenance' ? 'صيانة' : 'غير نشط'}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      <button className="action-btn" onClick={(e) => { e.stopPropagation(); handleEdit(v); }}>{I.edit}</button>
                      <button className="action-btn action-btn--danger" onClick={(e) => { e.stopPropagation(); handleDelete(v.id); }}>{I.trash}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingVehicle ? 'تعديل مركبة' : 'إضافة مركبة جديدة'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group"><label>رقم اللوحة *</label><input type="text" value={formData.plate_number} onChange={(e) => setFormData({...formData, plate_number: e.target.value})} required /></div>
                  <div className="form-group"><label>الشركة المصنعة</label><input type="text" value={formData.make} onChange={(e) => setFormData({...formData, make: e.target.value})} /></div>
                  <div className="form-group"><label>الموديل</label><input type="text" value={formData.model} onChange={(e) => setFormData({...formData, model: e.target.value})} /></div>
                  <div className="form-group"><label>سنة الصنع</label><input type="number" value={formData.year} onChange={(e) => setFormData({...formData, year: e.target.value})} /></div>
                  <div className="form-group"><label>السعة (طن)</label><input type="number" step="0.1" value={formData.capacity_tons} onChange={(e) => setFormData({...formData, capacity_tons: e.target.value})} /></div>
                  <div className="form-group">
                    <label>نوع الوقود</label>
                    <select value={formData.fuel_type} onChange={(e) => setFormData({...formData, fuel_type: e.target.value})}>
                      <option value="">اختر</option>
                      <option value="petrol">بنزين</option>
                      <option value="diesel">ديزل</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">{editingVehicle ? 'حفظ التعديلات' : 'إضافة'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiclesPage;
